import pick from 'lodash/pick';
import {
  createStripeAccount,
  updateStripeAccount,
  fetchStripeAccount,
} from '../../ducks/stripeConnectAccount.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { createStripeTransferForProvider, fetchFreeTransactions } from '../../util/api';
import { parse } from '../../util/urlHelpers';
import moment from 'moment';
import { isRefunded } from '../../transactions/transactionProcessBookingFree';
import { transitions } from '../../transactions/transactionProcessPurchase';

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/StripePayoutPage/SET_INITIAL_VALUES';
export const SAVE_PAYOUT_DETAILS_REQUEST = 'app/StripePayoutPage/SAVE_PAYOUT_DETAILS_REQUEST';
export const SAVE_PAYOUT_DETAILS_SUCCESS = 'app/StripePayoutPage/SAVE_PAYOUT_DETAILS_SUCCESS';
export const SAVE_PAYOUT_DETAILS_ERROR = 'app/StripePayoutPage/SAVE_PAYOUT_DETAILS_ERROR';

export const FETCH_TRANSACTION_DATA_REQUEST = 'app/StripePayoutPage/FETCH_TRANSACTION_DATA_REQUEST';
export const FETCH_TRANSACTION_DATA_SUCCESS = 'app/StripePayoutPage/FETCH_TRANSACTION_DATA_SUCCESS';
export const FETCH_TRANSACTION_DATA_ERROR = 'app/StripePayoutPage/FETCH_TRANSACTION_DATA_ERROR';

export const CREATE_TRANSFER_REQUEST = 'app/StripePayoutPage/CREATE_TRANSFER_REQUEST';
export const CREATE_TRANSFER_SUCCESS = 'app/StripePayoutPage/CREATE_TRANSFER_SUCCESS';
export const CREATE_TRANSFER_ERROR = 'app/StripePayoutPage/CREATE_TRANSFER_ERROR';
// ================ Reducer ================ //

const initialState = {
  payoutDetailsSaveInProgress: false,
  payoutDetailsSaved: false,
  fromReturnURL: false,
  // Payout Transactions
  transactionData: [],
  fetchTransactionInProgress: false,
  fetchTransactionError: null,
  pagination: {
    page: 1,
    perPage: 100,
    totalItems: 0,
    totalPages: 0,
  },
  totalProivderAmount: 0,
  availableToWithdrawAmount: 0,
  allreadyTransferredAmount: 0,
  baseCurrency: 'USD',

  createTransferInProgress: false,
  createTransferError: null,
  createTransferSuccess: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SAVE_PAYOUT_DETAILS_REQUEST:
      return { ...state, payoutDetailsSaveInProgress: true };
    case SAVE_PAYOUT_DETAILS_ERROR:
      return { ...state, payoutDetailsSaveInProgress: false };
    case SAVE_PAYOUT_DETAILS_SUCCESS:
      return { ...state, payoutDetailsSaveInProgress: false, payoutDetailsSaved: true };

    case FETCH_TRANSACTION_DATA_REQUEST:
      return { ...state, fetchTransactionInProgress: true, fetchTransactionError: null };
    case FETCH_TRANSACTION_DATA_SUCCESS:
      return { ...state, fetchTransactionInProgress: false, transactionData: payload.transactionData, pagination: payload.pagination, totalProivderAmount: payload.totalProivderAmount || 0, availableToWithdrawAmount: payload.availableToWithdrawAmount || 0, allreadyTransferredAmount: payload.allreadyTransferredAmount || 0, baseCurrency: payload.baseCurrency || 'USD' };
    case FETCH_TRANSACTION_DATA_ERROR:
      return { ...state, fetchTransactionInProgress: false, fetchTransactionError: payload };

    case CREATE_TRANSFER_REQUEST:
      return { ...state, createTransferInProgress: true, createTransferError: null };
    case CREATE_TRANSFER_SUCCESS:
      return { ...state, createTransferInProgress: false, createTransferSuccess: true, createTransferError: null };
    case CREATE_TRANSFER_ERROR:
      return { ...state, createTransferInProgress: false, createTransferError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

export const savePayoutDetailsRequest = () => ({
  type: SAVE_PAYOUT_DETAILS_REQUEST,
});
export const savePayoutDetailsError = () => ({
  type: SAVE_PAYOUT_DETAILS_ERROR,
});
export const savePayoutDetailsSuccess = () => ({
  type: SAVE_PAYOUT_DETAILS_SUCCESS,
});

export const fetchTransactionDataRequest = () => ({
  type: FETCH_TRANSACTION_DATA_REQUEST,
});
export const fetchTransactionDataSuccess = (data) => ({
  type: FETCH_TRANSACTION_DATA_SUCCESS,
  payload: data,
});
export const fetchTransactionDataError = (error) => ({
  type: FETCH_TRANSACTION_DATA_ERROR,
  payload: error,
});
export const createTransferRequest = () => ({
  type: CREATE_TRANSFER_REQUEST,
});
export const createTransferSuccess = () => ({
  type: CREATE_TRANSFER_SUCCESS,
});
export const createTransferError = (error) => ({
  type: CREATE_TRANSFER_ERROR,
  payload: error,
});
// ================ Thunks ================ //

// Transform API response data into display format
const transformTransactionData = (apiResponse) => {
  if (!apiResponse || !apiResponse.data || !Array.isArray(apiResponse.data)) {
    return [];
  }

  const transformedData = apiResponse.data.map((transaction) => {
    // Divide amount by 100 (convert from cents to dollars)
    const amount = transaction.tx_amount ? transaction.tx_amount / 100 : 0;
    const providerAmount = transaction?.tx_provider_amount ? transaction?.tx_provider_amount / 100 : 0;

    // Format date - use createdAt from mongoose timestamps, or current date as fallback
    const dateObj = transaction.createdAt
      ? new Date(transaction.createdAt)
      : (transaction.updatedAt ? new Date(transaction.updatedAt) : new Date());
    const formattedDate = moment(dateObj).format('MMM D, YYYY');
    const status = isRefunded(transaction.tx_status) ? 'refunded' : (transaction?.tx_status === transitions?.CONFIRM_PAYMENT ? 'pending'  : 'succeeded');
    // Get transaction ID - use tx_id or generate from MongoDB _id
    const transactionId = transaction.tx_id
      ? transaction.tx_id
      : (transaction._id ? transaction._id.toString() : 'TXN0000');

    return {
      id: transactionId,
      currency: transaction.tx_currency || 'USD',
      amount: parseFloat(amount.toFixed(2)),
      providerAmount: parseFloat(providerAmount?.toFixed(2)),
      status: status,
      date: formattedDate,
    };
  });

  return transformedData;
};

export const savePayoutDetails = (values, isUpdateCall) => (dispatch, getState, sdk) => {
  const upsertThunk = isUpdateCall ? updateStripeAccount : createStripeAccount;
  dispatch(savePayoutDetailsRequest());

  return dispatch(upsertThunk(values, { expand: true }))
    .then(response => {
      dispatch(savePayoutDetailsSuccess());
      return response;
    })
    .catch(() => dispatch(savePayoutDetailsError()));
};
export const createTransfer = () => (dispatch, getState, sdk) => {
  dispatch(createTransferRequest());
  return dispatch(fetchCurrentUser())
    .then(response => {
      const providerId = getState().user.currentUser?.id?.uuid;
      return createStripeTransferForProvider({
        providerId
      })
        .then(response => {
          dispatch(createTransferSuccess());
          dispatch(loadData())
        })
        .catch(error => {
          // Extract error properties into a plain object
          // Error objects from api.js have properties assigned via Object.assign
          const errorObject = {
            message: error.message || 'An error occurred',
            status: error.status || 400,
          };
          // Copy all enumerable properties from the error object
          Object.keys(error).forEach(key => {
            errorObject[key] = error[key];
          });
          dispatch(createTransferError(errorObject));
        })
    })
};

export const loadData = (params, search) => (dispatch, getState, sdk) => {
  // Clear state so that previously loaded data is not visible
  // in case this page load fails.
  dispatch(setInitialValues());
  dispatch(fetchTransactionDataRequest());

  return dispatch(fetchCurrentUser()).then(response => {
    const currentUser = getState().user.currentUser;

    if (currentUser && currentUser.stripeAccount) {
      dispatch(fetchStripeAccount());
    }

    // Parse search params to get page number
    const searchParams = search ? parse(search) : {};
    const currentPage = searchParams.page ? parseInt(searchParams.page, 10) : 1;
    const perPage = 10;

    // Get current user ID to filter transactions
    const currentUserId = currentUser?.id?.uuid;

    if (!currentUserId) {
      dispatch(fetchTransactionDataError('User ID not found'));
      return Promise.reject(new Error('User ID not found'));
    }

    // Fetch transactions for current user
    return fetchFreeTransactions({
      payload: { provider_id: currentUserId },
      pageParams: {
        page: currentPage,
        perPage: perPage,
      }
    })
      .then(response => {
        // Transform the API response data
        const transformedData = transformTransactionData(response);

        // Extract pagination info from API response
        const pagination = response.pagination || {
          page: currentPage,
          perPage: perPage,
          totalItems: 0,
          totalPages: 0,
        };

        dispatch(fetchTransactionDataSuccess({
          transactionData: transformedData,
          pagination: pagination,
          totalProivderAmount: response.totalPlatFormAmount || 0,
          availableToWithdrawAmount: response.availableToWithdrawAmount || 0,
          allreadyTransferredAmount: response.allreadyTransferredAmount || 0,
          baseCurrency: response.baseCurrency || 'USD',
        }));

        return response;
      })
      .catch(error => {
        dispatch(fetchTransactionDataError(error));
        throw error;
      });
  }).catch(error => {
    dispatch(fetchTransactionDataError(error));
    throw error;
  });
};
